import { inventoryTotals, type InventoryItem, type ItemSuggestion } from '@kartothek/shared';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { api } from '../api';
import { useNoteIndex } from '../hooks';
import { t } from '../i18n';
import { Combobox, type ComboOption } from './Combobox';
import { NoteLink } from './NoteLink';

const num = (n: number | undefined, digits = 2) => (n === undefined ? '' : n.toLocaleString('de-DE', { maximumFractionDigits: digits }));

/** „0,5“ / „1.250“ / „“ → Zahl bzw. undefined. */
function parseNum(raw: string): number | undefined {
  const s = raw.trim().replace(/\s*(gm|gp|kg)$/i, '').replace(/\.(?=\d{3}\b)/g, '').replace(',', '.');
  if (s === '') return undefined;
  const n = Number(s);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}

function hintOf(x: ItemSuggestion): string {
  const parts = [x.detail ?? '', x.value !== undefined ? `${num(x.value)} ${t('field.goldUnit')}` : '', x.weight !== undefined ? `${num(x.weight, 3)} kg` : ''];
  return parts.filter(Boolean).join(' · ');
}

/** Zahl, die beim Verlassen des Felds übernommen wird. */
function NumCell(props: { value: number | undefined; onCommit: (n: number | undefined) => void; label: string; digits?: number }) {
  return (
    <input
      className="inv-num"
      inputMode="decimal"
      aria-label={props.label}
      defaultValue={num(props.value, props.digits)}
      key={String(props.value)}
      onBlur={(e) => {
        const n = parseNum(e.target.value);
        if (n !== props.value) props.onCommit(n);
      }}
      onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
    />
  );
}

/**
 * Inventar eines Charakters: Name, Anzahl, Wert (GM) und Gewicht (kg) je Stück.
 * Neue Gegenstände per Freitext; Vorschläge aus der Kampagne und der Grundausrüstung übernehmen Wert und Gewicht.
 */
export function Inventory(props: { cid: string; items: InventoryItem[]; editable: boolean; onChange: (items: InventoryItem[]) => void }) {
  const { items } = props;
  const totals = inventoryTotals(items);
  const index = useNoteIndex(props.cid);
  const itemNotes = useMemo(
    () => new Map((index.data ?? []).filter((n) => n.typeKey === 'gegenstand').map((n) => [n.title.toLowerCase(), n.id])),
    [index.data],
  );
  const suggestions = useQuery({ queryKey: ['items', props.cid], queryFn: () => api.items(props.cid), enabled: props.editable, staleTime: 60_000 });
  const options = useMemo<(ComboOption & { item: ItemSuggestion })[]>(
    () => (suggestions.data ?? []).map((x) => ({ key: x.name.toLowerCase(), title: x.name, hint: hintOf(x), item: x })),
    [suggestions.data],
  );

  const [name, setName] = useState('');
  const [qty, setQty] = useState('1');
  const [value, setValue] = useState('');
  const [weight, setWeight] = useState('');
  const qtyRef = useRef<HTMLInputElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  const update = (i: number, patch: Partial<InventoryItem>) => props.onChange(items.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  const add = () => {
    const n = name.trim();
    if (!n) return;
    const count = Math.max(1, Math.floor(parseNum(qty) ?? 1));
    const same = items.findIndex((x) => x.name.toLowerCase() === n.toLowerCase());
    if (same >= 0) {
      // Gleicher Gegenstand: nur die Anzahl erhöhen
      update(same, { qty: items[same]!.qty + count });
    } else {
      const item: InventoryItem = { name: n, qty: count };
      const v = parseNum(value);
      const w = parseNum(weight);
      if (v !== undefined) item.value = v;
      if (w !== undefined) item.weight = w;
      props.onChange([...items, item]);
    }
    setName('');
    setQty('1');
    setValue('');
    setWeight('');
    nameRef.current?.focus();
  };

  if (!props.editable && items.length === 0) return null;
  return (
    <table className="inventory">
      <thead>
        <tr>
          <th>{t('inventory.item')}</th>
          <th className="num">{t('inventory.qty')}</th>
          <th className="num">{t('inventory.value')}</th>
          <th className="num">{t('inventory.weight')}</th>
          {props.editable && <th />}
        </tr>
      </thead>
      <tbody>
        {items.map((x, i) => {
          const noteId = itemNotes.get(x.name.toLowerCase());
          return (
            <tr key={`${x.name}-${i}`}>
              <td>
                {noteId ? (
                  <NoteLink cid={props.cid} noteId={noteId}>
                    {x.name}
                  </NoteLink>
                ) : (
                  x.name
                )}
              </td>
              {props.editable ? (
                <>
                  <td className="num">
                    <NumCell value={x.qty} digits={0} label={t('inventory.qty')} onCommit={(n) => update(i, { qty: Math.max(0, Math.floor(n ?? 0)) })} />
                  </td>
                  <td className="num">
                    <NumCell value={x.value} label={t('inventory.value')} onCommit={(n) => update(i, { value: n })} />
                  </td>
                  <td className="num">
                    <NumCell value={x.weight} digits={3} label={t('inventory.weight')} onCommit={(n) => update(i, { weight: n })} />
                  </td>
                  <td>
                    <button className="icon-btn danger" title={t('inventory.remove')} onClick={() => props.onChange(items.filter((_, j) => j !== i))}>
                      ×
                    </button>
                  </td>
                </>
              ) : (
                <>
                  <td className="num">{x.qty}</td>
                  <td className="num">{num(x.value)}</td>
                  <td className="num">{num(x.weight, 3)}</td>
                </>
              )}
            </tr>
          );
        })}
        {props.editable && (
          <tr className="inv-add">
            <td>
              <Combobox
                value={name}
                onChange={setName}
                options={options}
                showAllWhenEmpty={false}
                limit={12}
                inputRef={nameRef}
                aria-label={t('inventory.item')}
                placeholder={t('inventory.addPlaceholder')}
                onPick={(o) => {
                  setName(o.title);
                  setValue(num(o.item.value));
                  setWeight(num(o.item.weight, 3));
                  qtyRef.current?.select();
                }}
                onEnter={add}
              />
            </td>
            <td className="num">
              <input ref={qtyRef} className="inv-num" inputMode="numeric" value={qty} aria-label={t('inventory.qty')} onChange={(e) => setQty(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} />
            </td>
            <td className="num">
              <input className="inv-num" inputMode="decimal" value={value} aria-label={t('inventory.value')} onChange={(e) => setValue(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} />
            </td>
            <td className="num">
              <input className="inv-num" inputMode="decimal" value={weight} aria-label={t('inventory.weight')} onChange={(e) => setWeight(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} />
            </td>
            <td>
              <button className="icon-btn" title={t('inventory.add')} disabled={!name.trim()} onClick={add}>
                +
              </button>
            </td>
          </tr>
        )}
      </tbody>
      {items.length > 0 && (
        <tfoot>
          <tr>
            <th>{t('inventory.total')}</th>
            <td />
            <td className="num">
              {num(totals.value)} {t('field.goldUnit')}
            </td>
            <td className="num">{num(totals.weight, 3)} kg</td>
            {props.editable && <td />}
          </tr>
        </tfoot>
      )}
    </table>
  );
}
